// src/components/blog/ShareButtons.tsx
//
// Share buttons for Twitter, LinkedIn, and copy-to-clipboard. Copy uses the
// Web Clipboard API with a toast confirmation.

'use client'

import { useState } from 'react'
import { Twitter, Linkedin, Link2, Check } from 'lucide-react'

export interface ShareButtonsProps {
  url: string
  title: string
}

export default function ShareButtons({ url, title }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false)

  const twitterUrl =
    'https://twitter.com/intent/tweet?text=' +
    encodeURIComponent(title) +
    '&url=' +
    encodeURIComponent(url)

  const linkedinUrl =
    'https://www.linkedin.com/sharing/share-offsite/?url=' + encodeURIComponent(url)

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: select-and-prompt if clipboard API is unavailable
      window.prompt('Copy this link', url)
    }
  }

  const iconClass =
    'inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition hover:border-brand/40 hover:text-brand-ink'

  return (
    <div className="flex items-center gap-2">
      <span className="mr-1 text-sm text-muted-foreground">Share:</span>
      <a
        href={twitterUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Twitter"
        className={iconClass}
      >
        <Twitter className="h-4 w-4" aria-hidden />
      </a>
      <a
        href={linkedinUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on LinkedIn"
        className={iconClass}
      >
        <Linkedin className="h-4 w-4" aria-hidden />
      </a>
      <button type="button" onClick={copyToClipboard} aria-label="Copy link" className={iconClass}>
        {copied ? (
          <Check className="h-4 w-4 text-emerald-700 dark:text-emerald-300" aria-hidden />
        ) : (
          <Link2 className="h-4 w-4" aria-hidden />
        )}
      </button>
    </div>
  )
}
